import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, Upload, Check, Search, Star, ExternalLink, AlertCircle } from "lucide-react";

function EmailPreview({ businessName, logoDataUrl }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="rounded-xl border border-slate-200 bg-white p-6 max-w-md mx-auto">
        {logoDataUrl ? (
          <img src={logoDataUrl} alt="logo" className="h-12 mb-3 object-contain" />
        ) : (
          <div className="h-12 w-32 bg-slate-100 rounded mb-3 grid place-items-center text-slate-400 text-xs">Your logo</div>
        )}
        <div className="font-display font-bold text-lg text-slate-900 mb-2">How did we do, [First name]?</div>
        <p className="text-sm text-slate-600 mb-2">Thanks so much for choosing us — it was a pleasure working with you.</p>
        <p className="text-sm text-slate-600 mb-4">Would you take 30 seconds to share your experience on Google? It genuinely helps <strong>{businessName || "your business"}</strong>.</p>
        <div className="my-4"><span className="inline-block bg-primary text-white rounded-full px-5 py-2.5 text-sm font-semibold">Leave us a review</span></div>
        <p className="text-xs text-slate-400 mt-3">Thanks so much,<br/>{businessName || "Your business"}</p>
      </div>
    </div>
  );
}

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [businessName, setBusinessName] = useState(user?.business_name || "");
  const [businessCity, setBusinessCity] = useState(user?.business_city || "");
  const [businessCountry, setBusinessCountry] = useState(user?.business_country || "US");
  const [logoDataUrl, setLogoDataUrl] = useState(user?.logo_data_url || "");
  const [query, setQuery] = useState("");
  const [enrichedQuery, setEnrichedQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [searchError, setSearchError] = useState(null); // null | "timeout" | "network" | "quota" | "unavailable"
  const [didSearch, setDidSearch] = useState(false);
  const [manualLink, setManualLink] = useState("");
  const [selected, setSelected] = useState(null); // {place_id, review_link, name}
  const [busy, setBusy] = useState(false);

  const onLogoChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setLogoDataUrl(reader.result);
    reader.readAsDataURL(f);
  };

  const searchPlaces = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setSearchError(null);
    setDidSearch(true);
    try {
      // Pass city + country captured in step 1. Server-side enrichment
      // covers the bare-name case; regionCode (CLDR two-char) covers the
      // ambiguous-city case that enrichment cannot — "Victoria" exists in
      // both BC and AU, and the owner should never see the Australian one.
      const { data } = await api.post("/places/search", {
        query, city: businessCity, country: businessCountry,
      });
      setResults(data.results || []);
      setEnrichedQuery(data.enriched_query || "");
      // Distinct error state: `error: null` means Google searched and found
      // nothing (empty state, coachable). Anything else means the integration
      // failed (outage state, paste-link is the path). Same message covering
      // both would make an outage look like a nonexistent business.
      setSearchError(data.error || null);
    } catch (e) {
      setResults([]);
      setEnrichedQuery("");
      setSearchError("network");
    } finally { setSearching(false); }
  };

  const step1Next = () => {
    if (!businessName.trim()) { toast.error("Enter your business name."); return; }
    if (!businessCity.trim()) { toast.error("Enter your city — helps us find your Google listing."); return; }
    // Pre-fill the step 2 search box with the business name the owner just
    // typed. Asking them to retype it in step 2 is friction at the exact
    // moment we are asking them to trust us. They can still edit it.
    if (!query.trim()) setQuery(businessName.trim());
    setStep(2);
  };
  const step2Next = () => {
    if (manualLink.trim()) {
      setSelected({ review_link: manualLink.trim(), name: businessName, place_id: "" });
      setStep(3);
      return;
    }
    if (!selected) { toast.error("Pick your business or paste your review link."); return; }
    setStep(3);
  };

  const finish = async () => {
    if (!selected?.review_link) { toast.error("Missing review link."); return; }
    setBusy(true);
    try {
      await api.post("/onboarding/complete", {
        business_name: businessName,
        business_city: businessCity,
        business_country: businessCountry,
        logo_data_url: logoDataUrl || "",
        google_review_link: selected.review_link,
        place_id: selected.place_id || "",
      });
      toast.success("You're all set!");
      await refresh();
      nav("/dashboard");
    } catch (e) {
      toast.error("Could not save. Try again.");
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      // ensure current values persisted for test
      await api.patch("/business", { business_name: businessName, logo_data_url: logoDataUrl, google_review_link: selected?.review_link });
      await api.post("/email/test", {});
      toast.success("Test email sent to " + user.email);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test email failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-6" data-testid="onboarding-progress">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`flex-1 h-1.5 rounded-full ${step >= n ? "bg-primary" : "bg-slate-200"}`} />
          ))}
        </div>
        <p className="text-sm text-slate-500 mb-6">Step {step} of 3</p>

        <Card className="p-8 rounded-2xl">
          {step === 1 && (
            <div>
              <h1 className="font-display text-2xl font-bold mb-1">Your business</h1>
              <p className="text-slate-500 mb-6 text-sm">This is what customers will see in emails.</p>
              <div className="space-y-5">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Business name</label>
                  <Input data-testid="onboarding-business-name" value={businessName} onChange={(e) => setBusinessName(e.target.value)} className="h-12 rounded-lg" placeholder="e.g. Ace Plumbing" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">City</label>
                  <Input data-testid="onboarding-business-city" value={businessCity} onChange={(e) => setBusinessCity(e.target.value)} className="h-12 rounded-lg" placeholder="e.g. Victoria BC or Chicago" />
                  <p className="text-xs text-slate-500 mt-2">We'll use this to find your Google listing in the next step.</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Country</label>
                  <select
                    data-testid="onboarding-business-country"
                    value={businessCountry}
                    onChange={(e) => setBusinessCountry(e.target.value)}
                    className="h-12 rounded-lg border border-slate-300 bg-white px-3 w-full text-sm"
                  >
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="GB">United Kingdom</option>
                    <option value="AU">Australia</option>
                    <option value="IE">Ireland</option>
                    <option value="NZ">New Zealand</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-2">Biases the Google search to the right country — protects against name collisions across borders.</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Logo (optional)</label>
                  <div className="flex items-center gap-3">
                    <label className="cursor-pointer inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 hover:bg-slate-50 text-sm font-medium">
                      <Upload className="h-4 w-4" /> Choose image
                      <input data-testid="onboarding-logo-input" type="file" accept="image/*" onChange={onLogoChange} className="hidden" />
                    </label>
                    {logoDataUrl && <img src={logoDataUrl} alt="logo" className="h-12 rounded border border-slate-200" />}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">If you skip, we'll show your business name as text.</p>
                </div>
              </div>
              <div className="flex justify-end mt-8">
                <Button data-testid="onboarding-step1-next" onClick={step1Next} className="rounded-full h-11 px-6 bg-primary hover:bg-primary/90">Next</Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h1 className="font-display text-2xl font-bold mb-1">Find your Google review link</h1>
              <p className="text-slate-500 mb-6 text-sm">Search your business name and city — we'll set the review link automatically.</p>
              <div className="flex gap-2">
                <Input data-testid="onboarding-places-query" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchPlaces()} placeholder="e.g. Ace Plumbing Chicago" className="h-12 rounded-lg" />
                <Button data-testid="onboarding-places-search" onClick={searchPlaces} disabled={searching} className="rounded-lg h-12 px-5 bg-primary hover:bg-primary/90">
                  {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>

              {results.length > 0 && (
                <div className="mt-4 space-y-2 max-h-96 overflow-y-auto" data-testid="onboarding-results">
                  {results.map((r) => (
                    <button key={r.place_id} data-testid={`place-result-${r.place_id}`} onClick={() => setSelected(r)}
                      className={`w-full text-left p-4 rounded-xl border transition-colors ${selected?.place_id === r.place_id ? "border-primary bg-primary/5" : "border-slate-200 hover:border-slate-300"}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 truncate">{r.name}</div>
                          <div className="text-sm text-slate-500 truncate">{r.address}</div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-600">
                            {r.rating ? (<span className="inline-flex items-center gap-1"><Star className="h-3 w-3 fill-yellow-400 stroke-yellow-400" /> {r.rating} · {r.user_ratings_total} reviews</span>) : <span className="text-amber-600 inline-flex items-center gap-1"><AlertCircle className="h-3 w-3"/> No reviews yet — may be unclaimed</span>}
                          </div>
                        </div>
                        {selected?.place_id === r.place_id && <Check className="h-5 w-5 text-primary" />}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {results.length > 0 && enrichedQuery && enrichedQuery !== query && (
                <p className="mt-3 text-xs text-slate-500" data-testid="onboarding-enriched-query">
                  Searched: <span className="font-mono">{enrichedQuery}</span>
                </p>
              )}

              {results.length === 0 && didSearch && !searching && searchError === null && (
                <div className="mt-4 rounded-xl bg-blue-50 border border-blue-100 p-4 text-sm text-blue-900" data-testid="onboarding-no-results">
                  <div className="font-semibold mb-1">No match yet</div>
                  <div>Try adding your city — e.g. <span className="font-mono">Irish Times Pub Victoria BC</span>. If your listing is unclaimed, Google won't return it.</div>
                </div>
              )}
              {results.length === 0 && didSearch && !searching && searchError !== null && (
                <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900" data-testid="onboarding-search-error">
                  <div className="font-semibold mb-1">Search is temporarily unavailable</div>
                  <div>Our Google Places lookup is having trouble ({searchError}). No problem — paste your review link below and you're set. We've logged the outage on our side.</div>
                </div>
              )}

              <div className="mt-6 rounded-xl border border-slate-200 p-4" data-testid="onboarding-manual-block">
                <div className="text-sm font-semibold text-slate-800 mb-1">Can't find it? Paste your review link instead</div>
                <div className="mt-2 space-y-2">
                  <Input data-testid="onboarding-manual-link" value={manualLink} onChange={(e) => setManualLink(e.target.value)} placeholder="https://g.page/r/..." className="h-12 rounded-lg" />
                  <p className="text-xs text-slate-500">Paste the link Google gives you at business.google.com → "Ask for reviews".</p>
                </div>
              </div>

              <div className="mt-4 rounded-xl bg-amber-50 border border-amber-100 p-3 text-xs text-amber-900">
                Business profile unclaimed? <a href="https://business.google.com/create" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">Claim it on Google <ExternalLink className="h-3 w-3" /></a> — reviews only stick to claimed profiles.
              </div>

              <div className="flex justify-between mt-8">
                <Button variant="ghost" onClick={() => setStep(1)} data-testid="onboarding-step2-back">Back</Button>
                <Button data-testid="onboarding-step2-next" onClick={step2Next} className="rounded-full h-11 px-6 bg-primary hover:bg-primary/90">Next</Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h1 className="font-display text-2xl font-bold mb-1">Your review request email</h1>
              <p className="text-slate-500 mb-6 text-sm">This is exactly what your customer will see.</p>
              <EmailPreview businessName={businessName} logoDataUrl={logoDataUrl} />
              <div className="mt-4 flex flex-col sm:flex-row items-center gap-3">
                <Button variant="outline" onClick={sendTest} disabled={busy} className="rounded-full w-full sm:w-auto" data-testid="onboarding-test-email">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Send myself a test email
                </Button>
                <p className="text-xs text-slate-500">Test sends to {user?.email}</p>
              </div>
              <div className="flex justify-between mt-8">
                <Button variant="ghost" onClick={() => setStep(2)} data-testid="onboarding-step3-back">Back</Button>
                <Button data-testid="onboarding-finish" onClick={finish} disabled={busy} className="rounded-full h-11 px-6 bg-primary hover:bg-primary/90">
                  {busy ? "Saving…" : "Finish setup"}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
